import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './input';
import { getCurrencyMeta } from '../../lib/currencies';

// Função para formatar número em moeda brasileira (para exibição)
export const formatMoney = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 'R$ 0,00';
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

// Função para formatar valores grandes de forma resumida
export const formatMoneyShort = (value) => {
  if (!value && value !== 0) return 'R$ 0';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 'R$ 0';
  
  if (numValue >= 1000000000) {
    return `R$ ${(numValue / 1000000000).toFixed(1).replace('.', ',')}B`;
  }
  if (numValue >= 1000000) {
    return `R$ ${(numValue / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (numValue >= 1000) {
    return `R$ ${(numValue / 1000).toFixed(1).replace('.', ',')}K`;
  }
  return formatMoney(numValue);
};

export const CurrencyInput = ({ value, onChange, placeholder = "0,00", className = "", currency = 'BRL', ...props }) => {
  const [displayValue, setDisplayValue] = useState('');
  const meta = getCurrencyMeta(currency);
  const decimals = meta.decimals ?? 2;
  const factor = Math.pow(10, decimals);
  
  // Formata valor numérico para exibição
  const formatForDisplay = useCallback((numValue) => {
    if (numValue === null || numValue === undefined || numValue === '') return '';
    const num = typeof numValue === 'string' ? parseFloat(numValue) : numValue;
    if (isNaN(num) || num === 0) return '';
    
    return new Intl.NumberFormat(meta.locale || 'pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  }, [meta.locale, decimals]);
  
  // Atualiza display quando value externo muda
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '' && value !== 0) {
      setDisplayValue(formatForDisplay(value));
    } else {
      setDisplayValue('');
    }
  }, [value, formatForDisplay]);
  
  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Remove tudo exceto números
    const numbers = inputValue.replace(/\D/g, '');
    
    if (!numbers) {
      setDisplayValue('');
      onChange?.(0);
      return;
    }
    
    // Limita a 13 dígitos para evitar problemas de precisão
    const safeNumbers = numbers.slice(0, 13);
    
    // Converte para valor numérico (subunidades conforme casas decimais da moeda)
    const subunidades = parseInt(safeNumbers, 10);
    const reais = subunidades / factor;
    
    // Formata para exibição
    const formatted = new Intl.NumberFormat(meta.locale || 'pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(reais);
    
    setDisplayValue(formatted);
    onChange?.(reais);
  };
  
  const handleBlur = () => {
    // Ao sair do campo, garante formatação correta
    if (value && value !== 0) {
      setDisplayValue(formatForDisplay(value));
    }
  };
  
  const handleFocus = (e) => {
    // Seleciona todo o texto ao focar
    setTimeout(() => e.target.select(), 0);
  };
  
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
        {meta.symbol}
      </span>
      <Input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={`pl-10 ${className}`}
        {...props}
      />
    </div>
  );
};

export default CurrencyInput;
